import React from 'react';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { getValidAllergies, getValidFoodIntolerances, getValidPreferences } from '@/lib/clientPreferenceOptions';
import type { Client } from '@/types/client';
import layer1Graphic from '@/assets/images/layer-1.svg';
import { ClientNameLink } from '@/components/clients/ClientNameLink';
import { formatDisplayLabel } from '@/utils/display-labels';
import { getActiveMedicalConditionLabels } from '@/utils/client-medical-conditions';
import { getClientDailyCalorieGoalPreview } from '@/clients/details/v2/components/clientDetailsV2.utils';
import {
  ClientSummaryReadOnlyPopovers,
  type ClientSummaryReadOnlyPopoverKind,
} from './ClientSummaryReadOnlyPopovers';

const toTitleCase = (value: string): string =>
  value
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');

type ClientSummaryStripTitle = React.ReactNode | ((clientDisplayName: string, clientNameLink: React.ReactNode) => React.ReactNode);

const resolveAllergyPreviewLabel = (value: string): string => {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return '';
  const normalized = trimmed.toLowerCase() === 'sulfites' ? 'sulfite' : trimmed;
  return getValidAllergies().find((item) => item.value.toLowerCase() === normalized.toLowerCase())?.name
    || formatDisplayLabel(normalized);
};

const resolveIntolerancePreviewLabel = (value: string): string => {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return '';
  const normalized = (() => {
    const lower = trimmed.toLowerCase();
    if (lower === 'gluten_non_coeliac') return 'gluten';
    if (lower === 'fodmaps') return 'fodmap';
    return trimmed;
  })();
  return getValidFoodIntolerances().find((item) => item.value.toLowerCase() === normalized.toLowerCase())?.name
    || formatDisplayLabel(normalized);
};

const resolvePreferencePreviewLabel = (value: string): string => {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return '';
  return getValidPreferences().find((item) => item.value.toLowerCase() === trimmed.toLowerCase())?.name
    || formatDisplayLabel(trimmed);
};

const formatListPreview = (items: string[], visibleCount = 2): string => {
  if (items.length === 0) return 'Not set';
  if (items.length <= visibleCount) return items.join(', ');
  return `${items.slice(0, visibleCount).join(', ')}, +${items.length - visibleCount} more`;
};

const getAgeFromDob = (dob: string | undefined): number | null => {
  const parsed = dob ? new Date(dob) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - parsed.getFullYear();
  const monthDiff = now.getMonth() - parsed.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < parsed.getDate())) {
    age -= 1;
  }
  return age >= 0 ? age : null;
};

type SummaryPanelCard = {
  id: ClientSummaryReadOnlyPopoverKind;
  title: string;
  preview: string;
};

export type MealSuggestionsClientSummaryStripProps = {
  client: Client | null;
  clientId: string;
  clinicalTags?: string[];
  /** Optional heading override when the same blue client summary strip is used outside meal suggestions. */
  title?: ClientSummaryStripTitle;
  /** Merged onto the outer `<section>` (defaults match manual nutrition page). */
  sectionClassName?: string;
  /** Optional top-right content on wider screens (e.g. AI draft “meals selected” count). */
  headerAside?: React.ReactNode;
};

/**
 * Blue “Meal Suggestions” strip: title with BMR/EER, summary cards, and read-only popovers.
 * Shared across manual nutrition analysis, AI meal plan setup, and AI draft review.
 */
export function MealSuggestionsClientSummaryStrip({
  client,
  clientId,
  clinicalTags = [],
  title,
  sectionClassName = 'mb-4 rounded-xl bg-gradient-to-r from-cyan-500 to-sky-600 px-5 py-4 text-white',
  headerAside,
}: MealSuggestionsClientSummaryStripProps) {
  const trimmedClientId = String(clientId ?? '').trim();
  const [summaryPopoverKind, setSummaryPopoverKind] = React.useState<ClientSummaryReadOnlyPopoverKind | null>(null);
  const [summaryPopoverAnchorEl, setSummaryPopoverAnchorEl] = React.useState<HTMLElement | null>(null);

  const clientDisplayName = React.useMemo(() => {
    const fromFullName = String(client?.fullName ?? '').trim();
    const fromFirstLast = [client?.firstName, client?.lastName]
      .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)
      .join(' ')
      .trim();
    return toTitleCase(fromFullName || fromFirstLast || 'your client');
  }, [client]);

  const mealSuggestionsBmrEerLabel = React.useMemo(() => {
    const bmr = Number(client?.bmr);
    const eer = Number(client?.eerOverride ?? client?.eer);
    const bmrText = Number.isFinite(bmr) && bmr > 0 ? `${Math.round(bmr)} kcal` : '—';
    const eerText = Number.isFinite(eer) && eer > 0 ? `${Math.round(eer)} kcal` : '—';
    return { bmrText, eerText };
  }, [client?.bmr, client?.eer, client?.eerOverride]);

  const clinicalTagLabels = React.useMemo(
    () =>
      clinicalTags
        .map((tag) => formatDisplayLabel(String(tag ?? '').trim()))
        .filter(Boolean),
    [clinicalTags],
  );

  const summaryPanelCards = React.useMemo((): SummaryPanelCard[] => {
    const age = getAgeFromDob(client?.dateOfBirth);
    const gender = String(client?.gender ?? '').trim();
    const activity = formatDisplayLabel(client?.activityLevel ?? '');
    const aboutParts = [
      age != null ? `${age} years` : '',
      gender ? toTitleCase(gender) : '',
      activity ? toTitleCase(activity) : '',
    ].filter(Boolean);

    const allergies = Array.isArray(client?.allergies)
      ? client.allergies.map((item) => resolveAllergyPreviewLabel(String(item ?? '').trim())).filter(Boolean)
      : [];
    const intolerances = Array.isArray(client?.foodIntolerances)
      ? client.foodIntolerances.map((item) => resolveIntolerancePreviewLabel(String(item ?? '').trim())).filter(Boolean)
      : [];
    const preferences = Array.isArray(client?.dietaryPreferences)
      ? client.dietaryPreferences.map((item) => resolvePreferencePreviewLabel(String(item ?? '').trim())).filter(Boolean)
      : [];
    const conditions = getActiveMedicalConditionLabels(client);

    const healthGoal = String(client?.healthGoal ?? '').trim();

    const cards: SummaryPanelCard[] = [
      {
        id: 'about',
        title: 'About',
        preview: aboutParts.length > 0 ? aboutParts.join(', ') : 'Not set',
      },
      {
        id: 'healthGoal',
        title: 'Health Goal',
        preview: healthGoal || 'Not set',
      },
      {
        id: 'preferences',
        title: 'Dietary Preferences',
        preview: formatListPreview(preferences, 2),
      },
      {
        id: 'allergies',
        title: 'Allergies',
        preview: formatListPreview(allergies, 2),
      },
      {
        id: 'intolerances',
        title: 'Intolerances',
        preview: formatListPreview(intolerances, 2),
      },
      {
        id: 'medical',
        title: 'Medical Conditions',
        preview: formatListPreview(conditions, 3),
      },
      {
        id: 'labReports',
        title: 'Lab Reports',
        preview: 'Latest abnormal biomarkers',
      },
      {
        id: 'clientTargets',
        title: 'Client Targets',
        preview: getClientDailyCalorieGoalPreview(client),
      },
    ];

    if (clinicalTagLabels.length > 0) {
      cards.push({
        id: 'clinicalTags',
        title: 'Clinical Tags',
        preview: formatListPreview(clinicalTagLabels, 3),
      });
    }

    return cards;
  }, [client, clinicalTagLabels]);

  if (!trimmedClientId) {
    return null;
  }

  const clientNameLink = (
    <ClientNameLink clientId={trimmedClientId} className="text-white hover:text-white focus-visible:text-white">
      {clientDisplayName}
    </ClientNameLink>
  );

  const headingContent =
    typeof title === 'function'
      ? title(clientDisplayName, clientNameLink)
      : title ?? (
          <>
            Meal Suggestions for {clientNameLink}{' '}
            <span className="whitespace-normal font-semibold text-white/95">
              (BMR {mealSuggestionsBmrEerLabel.bmrText}, EER {mealSuggestionsBmrEerLabel.eerText})
            </span>
          </>
        );

  return (
    <section data-demo-id="nutrition-client-banner" className={`relative overflow-hidden ${sectionClassName}`}>
      <div
        className="pointer-events-none absolute inset-0 z-0 flex justify-end"
        aria-hidden
      >
        <img
          src={layer1Graphic}
          alt=""
          className="block h-full w-[min(297px,52%)] object-cover object-right"
        />
      </div>
      <div className="relative z-10">
        {headerAside ? (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 flex-1">
              <h2 className="text-xl font-semibold">{headingContent}</h2>
            </div>
            <div className="flex shrink-0 flex-col text-left sm:items-end sm:text-right">{headerAside}</div>
          </div>
        ) : (
          <h2 className="text-xl font-semibold">{headingContent}</h2>
        )}
        <div className="mt-2 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
          {summaryPanelCards.map((card) => {
            const isCardOpen = summaryPopoverKind === card.id;
            const content = (
              <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2">
                <div className="min-w-0">
                  <p className="font-[Inter,sans-serif] text-xs uppercase tracking-[0.06em] text-white/80">
                    {card.title}
                  </p>
                  <p className="mt-1 font-[Inter,sans-serif] text-[0.9rem] font-semibold leading-snug truncate text-white">
                    {card.preview}
                  </p>
                </div>
                <KeyboardArrowDownRoundedIcon
                  fontSize="small"
                  className="shrink-0 text-white/95 transition-transform duration-200"
                  style={{ transform: isCardOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                />
              </div>
            );

            return (
              <button
                key={card.id}
                type="button"
                className="min-w-0 cursor-pointer rounded-lg border border-white/35 bg-white/10 px-3 py-2 text-left outline-none ring-white/40 transition-colors hover:bg-white/15 focus-visible:ring-2"
                onClick={(event) => {
                  if (summaryPopoverKind === card.id) {
                    setSummaryPopoverKind(null);
                    setSummaryPopoverAnchorEl(null);
                  } else {
                    setSummaryPopoverKind(card.id);
                    setSummaryPopoverAnchorEl(event.currentTarget);
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    if (summaryPopoverKind === card.id) {
                      setSummaryPopoverKind(null);
                      setSummaryPopoverAnchorEl(null);
                    } else {
                      setSummaryPopoverKind(card.id);
                      setSummaryPopoverAnchorEl(event.currentTarget);
                    }
                  }
                }}
              >
                {content}
              </button>
            );
          })}
        </div>
        <ClientSummaryReadOnlyPopovers
          client={client}
          clientId={trimmedClientId}
          clinicalTags={clinicalTagLabels}
          openKind={summaryPopoverKind}
          anchorEl={summaryPopoverAnchorEl}
          onClose={() => {
            setSummaryPopoverKind(null);
            setSummaryPopoverAnchorEl(null);
          }}
        />
      </div>
    </section>
  );
}
